import React, { useEffect, useRef, useState } from "react";
import { RxCaretDown } from "react-icons/rx";

interface Option {
    id: string;
    label: string;
}

interface MultiSelectProps {
    title: string;
    options: Option[];
    onSelect: (selectedIds: string[]) => void;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
    title,
    options,
    onSelect,
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [selected, setSelected] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");

    // Ref for detecting clicks outside the dropdown
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const toggleOption = (optionId: string) => {
        let newSelected: string[];
        if (selected.includes(optionId)) {
            newSelected = selected.filter((id) => id !== optionId);
        } else {
            newSelected = [...selected, optionId];
        }
        setSelected(newSelected);
        onSelect(newSelected);
    };

    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="relative inline-block w-48 h-12" ref={wrapperRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full h-full bg-neutral-50 border border-neutral-200 hover:bg-white text-black font-semibold py-2 px-4 rounded-lg flex justify-between items-center"
            >
                <div className="flex items-center">
                    <span>{title}</span>
                    {selected.length > 0 && (
                        <span className="ml-1 text-gray-600">({selected.length})</span>
                    )}
                </div>
                {/* Caret from react-icons, rotating when isOpen is true */}
                <RxCaretDown
                    size={22}
                    className={`ml-2 transform transition-transform ${isOpen ? "rotate-180" : ""
                        }`}
                />
            </button>
            {isOpen && (
                <div className="absolute mt-2 w-full bg-white shadow-lg rounded-lg z-10">
                    <div className="p-2">
                        <input
                            type="text"
                            placeholder="Search..."
                            className="w-full border rounded-lg px-2 py-1 mb-2"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <div className="max-h-60 overflow-y-auto">
                            {filteredOptions.map((option) => (
                                <div key={option.id} className="flex items-center p-2 hover:bg-gray-100">
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(option.id)}
                                        onChange={() => toggleOption(option.id)}
                                        className="mr-2"
                                    />
                                    <span>{option.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
